import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  CalendarDays, DollarSign, Receipt, CheckCircle, AlertCircle, Lock,
  Plus, Trash2, LogOut, Wallet, Clock, Landmark
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTL = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ status }) => {
  if (status === 'delivered') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-500/15 text-teal-300 border border-teal-500/30" data-testid="report-status-delivered">
        <CheckCircle className="w-3 h-3" /> Teslim Edildi
      </span>
    );
  }
  if (status === 'submitted') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="report-status-submitted">
        <Lock className="w-3 h-3" /> Gönderildi
      </span>
    );
  }
  if (status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30">
        <Clock className="w-3 h-3" /> Taslak
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-600/40 text-slate-300 border border-slate-500/40">
      Girilmedi
    </span>
  );
};

export const RefereePortal = ({ currentUser, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [active, setActive] = useState(null); // seçili maç (rapor modalı)
  const [collected, setCollected] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [online, setOnline] = useState({ on: false, name: '', amount: '' });
  const [volly, setVolly] = useState({ on: false, name: '', amount: '' });
  const [saving, setSaving] = useState(false);

  const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API}/referee/me/reservations`, getAuth());
      setData(res.data);
    } catch (e) {
      setLoadError(true);
      toast.error('Maç verileri alınamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openReport = (res) => {
    setActive(res);
    const initOnline = res.online_amount || 0;
    const initVolly = res.volly_amount || 0;
    const expected = (res.paid_via_transfer || res.invoice_issued) ? 0 : ((res.total_amount || 0) - (res.kapora || 0) - initOnline - initVolly);
    // Elime geçen nakit: kaydedilmiş değer varsa onu, yoksa beklenen nakiti ön-doldur
    setCollected(res.report_collected != null ? String(res.report_collected) : (expected > 0 ? String(expected) : ''));
    setExpenses((res.report_expenses || []).map(e => ({ description: e.description, amount: String(e.amount) })));
    setOnline({ on: !!(res.online_amount || res.online_name), name: res.online_name || '', amount: res.online_amount ? String(res.online_amount) : '' });
    setVolly({ on: !!(res.volly_amount || res.volly_name), name: res.volly_name || '', amount: res.volly_amount ? String(res.volly_amount) : '' });
  };
  const closeReport = () => { setActive(null); setCollected(''); setExpenses([]); setOnline({ on: false, name: '', amount: '' }); setVolly({ on: false, name: '', amount: '' }); };

  const addExpense = () => setExpenses([...expenses, { description: '', amount: '' }]);
  const removeExpense = (i) => setExpenses(expenses.filter((_, idx) => idx !== i));
  const updateExpense = (i, field, val) => setExpenses(expenses.map((e, idx) => idx === i ? { ...e, [field]: val } : e));

  const expensesTotal = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const onlineAmt = online.on ? (parseFloat(online.amount) || 0) : 0;
  const vollyAmt = volly.on ? (parseFloat(volly.amount) || 0) : 0;
  const previewNet = active
    ? (active.paid_via_transfer || active.invoice_issued
        ? 0
        : (active.total_amount || 0) - (active.referee_fee || 0) - expensesTotal - (active.kapora || 0) - onlineAmt - vollyAmt)
    : 0;
  const collectedNum = parseFloat(collected);
  // Hakemin elinE geçmesi beklenen nakit = Maç Tutarı − Kapora − Online − Volly (havale ise 0)
  const expectedCash = active
    ? ((active.paid_via_transfer || active.invoice_issued) ? 0 : (active.total_amount || 0) - (active.kapora || 0) - onlineAmt - vollyAmt)
    : 0;
  const mismatch = active && !isNaN(collectedNum) && Math.abs(collectedNum - expectedCash) > 0.01;

  const submitReport = async (doSubmit) => {
    if (!active) return;
    const cleanExpenses = expenses
      .filter(e => (e.description || '').trim() && !isNaN(parseFloat(e.amount)))
      .map(e => ({ description: e.description.trim(), amount: parseFloat(e.amount) }));
    if (doSubmit && online.on && onlineAmt > 0 && !online.name.trim()) {
      toast.error('Online ödeme yapanın ad soyadı zorunludur');
      return;
    }
    if (doSubmit && volly.on && vollyAmt > 0 && !volly.name.trim()) {
      toast.error('Volly puan ödemesi yapanın ad soyadı zorunludur');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/referee/me/reservations/${active.id}/report`, {
        collected: collected === '' ? null : parseFloat(collected),
        expenses: cleanExpenses,
        submit: doSubmit,
        online_name: online.on ? online.name.trim() : null,
        online_amount: online.on ? onlineAmt : 0,
        volly_name: volly.on ? volly.name.trim() : null,
        volly_amount: volly.on ? vollyAmt : 0,
      }, getAuth());
      toast.success(doSubmit ? 'Onaylandı ve gönderildi' : 'Taslak kaydedildi');
      closeReport();
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kaydedilemedi';
      toast.error(typeof msg === 'string' ? msg : 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const s = data?.summary || {};

  const renderTable = (rows, title, emptyText) => (
    <Card className="bg-slate-800/50 border-slate-700 mb-6">
      <div className="px-5 py-4 border-b border-slate-700 flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-blue-400" />
        <h3 className="text-white font-semibold">{title}</h3>
        <span className="ml-2 text-xs text-slate-400">({rows.length})</span>
      </div>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="text-slate-500 text-center py-10">{emptyText}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-3 text-slate-300 font-semibold">Tarih</th>
                  <th className="text-left p-3 text-slate-300 font-semibold">Saat</th>
                  <th className="text-left p-3 text-slate-300 font-semibold">Müşteri / Salon</th>
                  <th className="text-right p-3 text-slate-300 font-semibold">Maç Tutarı</th>
                  <th className="text-right p-3 text-slate-300 font-semibold">Payım</th>
                  <th className="text-right p-3 text-slate-300 font-semibold">Kapora</th>
                  <th className="text-right p-3 text-slate-300 font-semibold">Teslim Edeceğim</th>
                  <th className="text-center p-3 text-slate-300 font-semibold">Durum</th>
                  <th className="text-center p-3 text-slate-300 font-semibold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((res) => (
                  <tr key={res.id} className="border-t border-slate-700 hover:bg-slate-700/30" data-testid={`referee-match-row-${res.id}`}>
                    <td className="p-3 text-slate-300 whitespace-nowrap">{res.date}</td>
                    <td className="p-3 text-slate-400 whitespace-nowrap">{res.start_time?.slice(0, 5)}-{res.end_time?.slice(0, 5)}</td>
                    <td className="p-3">
                      <div className="text-white font-medium">{res.customer_name}</div>
                      <div className="text-slate-400 text-xs">{res.salon_name}</div>
                    </td>
                    <td className="p-3 text-right text-slate-300">{formatTL(res.total_amount)}</td>
                    <td className="p-3 text-right text-green-400 font-semibold">{formatTL(res.referee_fee)}</td>
                    <td className="p-3 text-right text-blue-300">{res.kapora ? `-${formatTL(res.kapora)}` : '—'}</td>
                    <td className="p-3 text-right text-orange-300 font-bold">{formatTL(res.net_to_deliver)}</td>
                    <td className="p-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <StatusBadge status={res.referee_payment_collected ? 'delivered' : res.report_status} />
                        {res.referee_payment_collected && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-500/15 text-teal-300 border border-teal-500/30" data-testid={`referee-collected-badge-${res.id}`} title="Yönetici bu maçın ödemesini sizden tahsil etti">
                            <Lock className="w-3 h-3" /> Tahsil Edildi
                          </span>
                        )}
                        {res.paid_via_transfer && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-cyan-300" title="Müşteri havale/IBAN ile bankaya ödedi">
                            <Landmark className="w-3 h-3" /> Havale
                          </span>
                        )}
                        {res.amount_mismatch && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-rose-400" title="Beyan edilen tahsilat maç tutarından farklı">
                            <AlertCircle className="w-3 h-3" /> Uyuşmuyor
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => openReport(res)}
                        data-testid={`referee-report-open-${res.id}`}
                        className={(res.report_status === 'submitted' || res.report_status === 'delivered' || res.referee_payment_collected)
                          ? 'bg-slate-600 hover:bg-slate-500 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'}
                      >
                        {(res.report_status === 'submitted' || res.report_status === 'delivered' || res.referee_payment_collected)
                          ? (<><Lock className="w-3 h-3 mr-1" /> Görüntüle</>)
                          : (<><Wallet className="w-3 h-3 mr-1" /> Gir</>)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const locked = active?.report_status === 'submitted' || active?.report_status === 'delivered' || active?.referee_payment_collected === true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="container mx-auto p-4 md:p-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-4">
          <div>
            <h1 className="text-xl font-bold text-white">Hakem Paneli</h1>
            <p className="text-slate-400 text-sm">Hoş geldiniz, <span className="text-blue-400 font-semibold">{currentUser?.referee_name}</span></p>
          </div>
          <Button onClick={onLogout} variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20" data-testid="referee-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Çıkış
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-400 text-center py-20">Yükleniyor...</p>
        ) : loadError ? (
          <div className="text-center py-20" data-testid="referee-load-error">
            <AlertCircle className="w-10 h-10 text-rose-400 mx-auto mb-3" />
            <p className="text-slate-300 mb-4">Maç verileri yüklenemedi.</p>
            <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700" data-testid="referee-retry-btn">Tekrar Dene</Button>
          </div>
        ) : (
          <>
            {/* Özet kartlar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-cyan-500 to-teal-600 text-white border-0"><CardContent className="pt-6">
                <div className="flex items-center gap-3"><div className="p-3 bg-white/20 rounded-xl"><CalendarDays className="w-6 h-6" /></div>
                  <div><p className="text-cyan-100 text-xs">Toplam Maç</p><p className="text-2xl font-bold" data-testid="referee-summary-matches">{s.total_matches || 0}</p></div></div>
              </CardContent></Card>
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0"><CardContent className="pt-6">
                <div className="flex items-center gap-3"><div className="p-3 bg-white/20 rounded-xl"><DollarSign className="w-6 h-6" /></div>
                  <div><p className="text-green-100 text-xs">Toplam Payım</p><p className="text-2xl font-bold">{formatTL(s.total_fee)}</p></div></div>
              </CardContent></Card>
              <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0"><CardContent className="pt-6">
                <div className="flex items-center gap-3"><div className="p-3 bg-white/20 rounded-xl"><Receipt className="w-6 h-6" /></div>
                  <div><p className="text-orange-100 text-xs">Teslim Edilecek</p><p className="text-2xl font-bold" data-testid="referee-summary-net">{formatTL(s.total_net_to_deliver)}</p></div></div>
              </CardContent></Card>
              <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0"><CardContent className="pt-6">
                <div className="flex items-center gap-3"><div className="p-3 bg-white/20 rounded-xl"><CheckCircle className="w-6 h-6" /></div>
                  <div><p className="text-purple-100 text-xs">Teslim Edildi / Bekleyen</p><p className="text-2xl font-bold" data-testid="referee-summary-delivered">{s.delivered_count || 0}<span className="text-purple-200 text-base"> / {s.pending_count || 0}</span></p></div></div>
              </CardContent></Card>
            </div>

            {/* Bilgi kartı */}
            <Card className="mb-6 bg-amber-500/10 border-amber-500/30"><CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <div className="text-sm text-slate-300">
                  <p className="font-semibold text-amber-400 mb-1">Nasıl Çalışır?</p>
                  <p>• Her maç için <strong>tahsil ettiğiniz tutarı</strong> ve <strong>yaptığınız harcamaları</strong> girin.</p>
                  <p>• <strong>Teslim Edeceğim</strong> = Maç Tutarı − Payım (hakem ücreti) − Harcamalar − Kapora.</p>
                  <p>• <strong>Onayla & Gönder</strong> dedikten sonra kayıt kilitlenir; değişiklik için yöneticinize başvurun.</p>
                </div>
              </div>
            </CardContent></Card>

            {renderTable(data?.upcoming || [], 'Yaklaşan Maçlar', 'Yaklaşan maçınız bulunmuyor')}
            {renderTable(data?.past || [], 'Geçmiş Maçlar', 'Geçmiş maçınız bulunmuyor')}
          </>
        )}
      </div>

      {/* Rapor Modalı */}
      <Dialog open={!!active} onOpenChange={(o) => !o && closeReport()}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="referee-report-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {locked && <Lock className="w-4 h-4 text-emerald-400" />} Maç Raporu
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {active && `${active.customer_name} • ${active.date} • ${active.salon_name}`}
            </DialogDescription>
          </DialogHeader>

          {active && (
            <div className="space-y-4">
              {locked && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" /> {active.report_status === 'delivered'
                    ? 'Bu maç teslim edildi olarak kapatıldı. Değişiklik için yöneticinize başvurun.'
                    : active.referee_payment_collected
                      ? 'Bu maçın ödemesi yönetici tarafından tahsil edildi. Kayıt kilitli; değişiklik için yöneticinize başvurun.'
                      : 'Bu rapor gönderildi ve kilitlendi. Değişiklik için yöneticinize başvurun.'}
                </div>
              )}

              {active.paid_via_transfer && (
                <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-sm flex items-center gap-2">
                  <Landmark className="w-4 h-4" /> Müşteri havale/IBAN ile bankaya ödedi — elinizde nakit olmadığı için teslim edeceğiniz tutar <strong>₺0</strong>'dır.
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-lg bg-slate-700/50"><p className="text-xs text-slate-400">Maç Tutarı</p><p className="font-bold">{formatTL(active.total_amount)}</p></div>
                <div className="p-3 rounded-lg bg-slate-700/50"><p className="text-xs text-slate-400">Payım</p><p className="font-bold text-green-400">{formatTL(active.referee_fee)}</p></div>
                <div className="p-3 rounded-lg bg-slate-700/50"><p className="text-xs text-slate-400">Kapora</p><p className="font-bold text-blue-300">{active.kapora ? formatTL(active.kapora) : '—'}</p></div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Tahsil Ettiğim Tutar — Elime Geçen Nakit (₺)</Label>
                  {!locked && (
                    <button type="button" onClick={() => setCollected(expectedCash > 0 ? String(expectedCash) : '0')} className="text-[11px] text-cyan-300 hover:text-cyan-200 underline" data-testid="referee-autofill-collected">
                      Beklenen: {formatTL(expectedCash)} — Otomatik doldur
                    </button>
                  )}
                </div>
                <Input
                  type="number" step="0.01" disabled={locked}
                  value={collected}
                  onChange={(e) => setCollected(e.target.value)}
                  placeholder={`Örn: ${expectedCash}`}
                  className="bg-slate-700 border-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="referee-collected-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Maç tutarından kapora, online ve Volly ödemeleri düşülünce elinize geçmesi beklenen nakit: <strong className="text-slate-200">{formatTL(expectedCash)}</strong>
                </p>
                {mismatch && (
                  <p className="text-xs text-rose-400 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Dikkat: Girdiğiniz tutar ({formatTL(collectedNum)}) beklenen nakitten ({formatTL(expectedCash)}) farklı.
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300">Harcamalarım</Label>
                  {!locked && (
                    <Button type="button" size="sm" onClick={addExpense} className="bg-red-500/20 text-red-300 hover:bg-red-500/30 h-8" data-testid="referee-add-expense-btn">
                      <Plus className="w-3 h-3 mr-1" /> Harcama Ekle
                    </Button>
                  )}
                </div>
                {expenses.length === 0 ? (
                  <p className="text-slate-500 text-sm">Harcama eklenmedi</p>
                ) : (
                  <div className="space-y-2">
                    {expenses.map((exp, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input disabled={locked} value={exp.description} onChange={(e) => updateExpense(i, 'description', e.target.value)} placeholder="Açıklama (örn: su, top)" className="bg-slate-700 border-slate-600 text-white flex-1" data-testid={`referee-expense-desc-${i}`} />
                        <Input disabled={locked} type="number" step="0.01" value={exp.amount} onChange={(e) => updateExpense(i, 'amount', e.target.value)} placeholder="₺" className="bg-slate-700 border-slate-600 text-white w-24" data-testid={`referee-expense-amount-${i}`} />
                        {!locked && (
                          <button type="button" onClick={() => removeExpense(i)} className="text-rose-400 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Nakit dışı ödemeler */}
              <div className="grid grid-cols-1 gap-2">
                {[{ key: 'online', st: online, set: setOnline, label: 'Online Ödeme' },
                  { key: 'volly', st: volly, set: setVolly, label: 'Volly Puan Ödemesi' }].map(({ key, st, set, label }) => (
                  <div key={key} className={`rounded-lg border p-3 ${st.on ? 'border-cyan-500/40 bg-cyan-500/10' : 'border-slate-600 bg-slate-700/30'}`}>
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => set({ ...st, on: !st.on })}
                      className="w-full flex items-center justify-between text-left"
                      data-testid={`referee-${key}-toggle`}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-white">
                        <Landmark className="w-4 h-4 text-cyan-300" /> {label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${st.on ? 'bg-cyan-500 text-white' : 'bg-slate-600 text-slate-300'}`}>
                        {st.on ? 'Açık' : 'Kapalı'}
                      </span>
                    </button>
                    {st.on && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-slate-300 text-xs">Ödeyen Ad Soyad *</Label>
                          <Input disabled={locked} value={st.name} onChange={(e) => set({ ...st, name: e.target.value })}
                            placeholder="Zorunlu" className="bg-slate-700 border-slate-600 text-white disabled:opacity-50"
                            data-testid={`referee-${key}-name`} />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Tutar (₺)</Label>
                          <Input disabled={locked} type="number" step="0.01" value={st.amount} onChange={(e) => set({ ...st, amount: e.target.value })}
                            placeholder="0" className="bg-slate-700 border-slate-600 text-white disabled:opacity-50"
                            data-testid={`referee-${key}-amount`} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-gradient-to-r from-orange-500/15 to-red-500/15 border border-orange-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-200">Yönetime Teslim Edeceğim Net</p>
                    <p className="text-xs text-slate-400">Maç Tutarı − Payım − Harcamalar − Kapora − Online − Volly</p>
                  </div>
                  <p className="text-2xl font-bold text-orange-300" data-testid="referee-net-preview">{formatTL(previewNet)}</p>
                </div>
              </div>

              {!locked && (
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" disabled={saving} onClick={() => submitReport(false)} className="flex-1 bg-slate-700 border-slate-600 text-white hover:bg-slate-600" data-testid="referee-save-draft-btn">
                    Taslak Kaydet
                  </Button>
                  <Button type="button" disabled={saving} onClick={() => submitReport(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="referee-submit-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Onayla & Gönder
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RefereePortal;
